import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import {
  EyeInvisibleOutlined,
  EyeOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { ConfigProvider, FloatButton, Pagination, Spin } from 'antd'
import { usePageOcr } from 'features/files/hooks/usePageOcr'
import * as React from 'react'
import { Document, pdfjs } from 'react-pdf'
import { useResizeDetector } from 'react-resize-detector'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

type PDFDocumentProxy = Awaited<ReturnType<typeof pdfjs.getDocument>['promise']>
type PageRef = Parameters<PDFDocumentProxy['getPageIndex']>[0]

const A4_ASPECT_RATIO = Math.SQRT2

import { type PdfHighlight, PdfPageWithOcr } from './PdfPageWithOcr'

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

export interface PdfViewerHighlight extends PdfHighlight {
  pageNumber: number
}

interface PdfViewerProps {
  url: string
  width?: number | string
  height?: number | string
  showPagination?: boolean
  continuousScroll?: boolean
  virtualize?: boolean
  className?: string
  initialPage?: number
  pageRotations?: Record<string, number>
  /**
   * Hide the floating OCR / text-selection action group in the lower-right
   * corner. Useful for compact previews (hover cards, thumbnails) where the
   * floating buttons would crowd the page or aren't actionable.
   */
  hideFloatActions?: boolean
  highlights?: PdfViewerHighlight[]
  onLoadSuccess?: (numPages: number) => void
  onLoadError?: (error: Error) => void
  onPageChange?: (page: number) => void
}

export interface PdfViewerHandle {
  /** Scroll to the given 1-based page number without remounting the viewer. */
  scrollToPage: (page: number) => void
  /** Scroll to the given normalized vertical location within a rendered 1-based page. */
  scrollToHighlight: (page: number, pageY: number) => void
}

const ViewerContainer = styled.div<{
  width?: number | string
  height?: number | string
}>`
  width: ${({ width }) =>
    typeof width === 'number' ? `${width}px` : width || '100%'};
  height: ${({ height }) =>
    typeof height === 'number' ? `${height}px` : height || '400px'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  position: relative;
`

const ScrollContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: ${CantileverTheme.colors.error[500]};
  text-align: center;
  padding: 16px;
`

const DocumentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
`

const PageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
`

const PagesContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px;
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background: ${CantileverTheme.colors.grayscale[100]};
  border-top: 1px solid ${CantileverTheme.colors.grayscale[300]};
`

export const RawPdfViewer = React.forwardRef<PdfViewerHandle, PdfViewerProps>(
  function RawPdfViewer(
    {
      url,
      width,
      height = 1000,
      showPagination = true,
      continuousScroll = false,
      virtualize = false,
      className,
      initialPage,
      pageRotations,
      hideFloatActions = false,
      highlights = [],
      onLoadError,
      onLoadSuccess,
      onPageChange,
    },
    imperativeRef
  ) {
    const [numPages, setNumPages] = React.useState<number | null>(null)
    const [currentPage, setCurrentPage] = React.useState<number>(
      initialPage || 1
    )
    const [loading, setLoading] = React.useState<boolean>(true)
    const [error, setError] = React.useState<string | null>(null)
    const [ocrEnabled, setOcrEnabled] = React.useState(false)
    const [ocrDebug, setOcrDebug] = React.useState(false)
    const pageRefsMap = React.useRef<Map<number, HTMLDivElement>>(new Map())
    const pdfDocRef = React.useRef<PDFDocumentProxy | null>(null)
    // Stores a requested page when scrollToPage is called before the PDF has
    // finished loading (pageRefsMap is empty). Cleared once the scroll fires.
    const pendingScrollPageRef = React.useRef<number | null>(null)
    const { width: detectedWidth, ref } = useResizeDetector<HTMLDivElement>({
      refreshMode: 'debounce',
      refreshRate: 250,
    })

    const scrollPage = React.useCallback(
      (
        targetEl: HTMLDivElement,
        block: ScrollLogicalPosition,
        pageY?: number
      ) => {
        const scrollEl = ref.current
        if (!scrollEl) {
          targetEl.scrollIntoView({ block })
          return
        }
        let targetTop = targetEl.offsetTop
        if (pageY !== undefined) {
          targetTop += targetEl.offsetHeight * pageY - scrollEl.clientHeight / 2
        } else if (block === 'center') {
          targetTop -= (scrollEl.clientHeight - targetEl.offsetHeight) / 2
        }
        scrollEl.scrollTo({ top: targetTop, behavior: 'auto' })
      },
      [ref]
    )

    React.useImperativeHandle(imperativeRef, () => ({
      scrollToPage: (page: number) => {
        const targetEl = pageRefsMap.current.get(page)
        if (targetEl) {
          targetEl.scrollIntoView({ block: 'start' })
          pendingScrollPageRef.current = null
        } else {
          pendingScrollPageRef.current = page
        }
      },
      scrollToHighlight: (page: number, pageY: number) => {
        const targetEl = pageRefsMap.current.get(page) ?? ref.current

        if (targetEl) {
          scrollPage(targetEl, 'start', pageY)
          pendingScrollPageRef.current = null
        } else {
          pendingScrollPageRef.current = page
        }
      },
    }))

    // When numPages changes (PDF has loaded and page divs are about to mount),
    // fire any pending scroll after a short tick to let the divs register in
    // pageRefsMap via setPageRef.
    React.useEffect(() => {
      if (!numPages || pendingScrollPageRef.current === null) return
      const pendingPage = pendingScrollPageRef.current
      const timer = setTimeout(() => {
        const targetEl = pageRefsMap.current.get(pendingPage)
        if (targetEl) {
          targetEl.scrollIntoView({ block: 'start' })
          pendingScrollPageRef.current = null
        }
      }, 50)
      return () => clearTimeout(timer)
    }, [numPages])

    // Virtualization state -- only meaningful when virtualize && continuousScroll
    const [visiblePagesSet, setVisiblePagesSet] = React.useState<Set<number>>(
      () => new Set([1, 2, 3])
    )
    const pageHeights = React.useRef<Map<number, number>>(new Map())
    const resizeObserverRef = React.useRef<ResizeObserver | null>(null)

    const { pageStates, runOcr, clearAll, terminate } = usePageOcr()

    const pageWidth = React.useMemo(() => {
      if (!detectedWidth) return undefined
      const calculatedWidth = detectedWidth - 32 // subtract horizontal padding
      return calculatedWidth > 0 ? calculatedWidth : undefined
    }, [detectedWidth])

    const estimatedPageHeight = React.useMemo(
      () => Math.round((pageWidth ?? 600) * A4_ASPECT_RATIO),
      [pageWidth]
    )

    const onDocumentLoadSuccess = React.useCallback(
      (pdf: PDFDocumentProxy) => {
        pdfDocRef.current = pdf
        setNumPages(pdf.numPages)
        setLoading(false)
        setError(null)
        onLoadSuccess?.(pdf.numPages)
      },
      [onLoadSuccess]
    )

    const onDocumentLoadError = React.useCallback(
      (error: Error) => {
        setError(error.message)
        setLoading(false)
        onLoadError?.(error)
      },
      [onLoadError]
    )

    // Reset everything when the URL changes
    React.useEffect(() => {
      setCurrentPage(initialPage || 1)
      setNumPages(null)
      setLoading(true)
      setError(null)
      setOcrEnabled(false)
      setOcrDebug(false)
      clearAll()
      setVisiblePagesSet(new Set([1, 2, 3]))
      pageHeights.current.clear()
      pdfDocRef.current = null
    }, [url, initialPage, clearAll])

    // Clean up Tesseract worker on unmount
    React.useEffect(() => {
      return () => {
        terminate()
      }
    }, [terminate])

    const handlePageChange = React.useCallback(
      (page: number) => {
        setCurrentPage(page)
        onPageChange?.(page)
      },
      [onPageChange]
    )

    const visiblePageRef = React.useRef<number>(1)

    // Track rendered page heights via ResizeObserver so placeholders are accurate
    React.useEffect(() => {
      if (!virtualize) return

      const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const pageNum = Number(
            (entry.target as HTMLElement).getAttribute('data-page')
          )
          if (!pageNum) return
          const h = entry.contentRect.height
          if (h > 0) {
            pageHeights.current.set(pageNum, h)
          }
        })
      })
      resizeObserverRef.current = observer

      return () => {
        observer.disconnect()
        resizeObserverRef.current = null
      }
    }, [virtualize])

    const setPageRef = React.useCallback(
      (pageNum: number) => (el: HTMLDivElement | null) => {
        const prev = pageRefsMap.current.get(pageNum)
        if (prev && prev !== el && resizeObserverRef.current) {
          resizeObserverRef.current.unobserve(prev)
        }
        if (el) {
          pageRefsMap.current.set(pageNum, el)
          resizeObserverRef.current?.observe(el)
        } else {
          pageRefsMap.current.delete(pageNum)
        }
      },
      []
    )

    React.useEffect(() => {
      if (!continuousScroll || !numPages) return
      if (!virtualize && !onPageChange) return

      const scrollEl = ref.current
      if (!scrollEl) return

      if (virtualize) {
        const intersecting = new Set<number>()

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const pageNum = Number(entry.target.getAttribute('data-page'))
              if (entry.isIntersecting) {
                intersecting.add(pageNum)
              } else {
                intersecting.delete(pageNum)
              }
            })

            setVisiblePagesSet((prev) => {
              if (
                prev.size === intersecting.size &&
                [...intersecting].every((p) => prev.has(p))
              ) {
                return prev
              }
              return new Set(intersecting)
            })

            if (onPageChange) {
              let bestPage = visiblePageRef.current
              let bestRatio = 0
              entries.forEach((entry) => {
                const pageNum = Number(entry.target.getAttribute('data-page'))
                if (entry.intersectionRatio > bestRatio) {
                  bestRatio = entry.intersectionRatio
                  bestPage = pageNum
                }
              })
              if (bestPage !== visiblePageRef.current) {
                visiblePageRef.current = bestPage
                onPageChange(bestPage)
              }
            }
          },
          {
            root: scrollEl,
            rootMargin: '100% 0px',
            threshold: [0, 0.5],
          }
        )

        pageRefsMap.current.forEach((el) => observer.observe(el))
        return () => observer.disconnect()
      }

      // Non-virtualized: original page-tracking-only observer
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const pageNum = Number(entry.target.getAttribute('data-page'))
              if (pageNum !== visiblePageRef.current) {
                visiblePageRef.current = pageNum
                onPageChange?.(pageNum)
              }
            }
          }
        },
        {
          root: scrollEl,
          // Shrink top and bottom by 50% each, creating a detection line at the center.
          // A small buffer (2%) ensures a page is always detected even between gaps.
          rootMargin: '-48% 0px -48% 0px',
          threshold: 0,
        }
      )

      pageRefsMap.current.forEach((el) => observer.observe(el))
      return () => observer.disconnect()
    }, [continuousScroll, virtualize, onPageChange, numPages, ref])

    // Intercept annotation layer internal link clicks for virtualized navigation.
    // When pages are virtualized, pdf.js can't find off-screen target pages in the
    // DOM so its built-in scrollPageIntoView silently fails. We use capture-phase
    // interception to handle the click before pdf.js, look up the annotation's
    // destination directly from the PDF data, and scroll to the target slot div.
    React.useEffect(() => {
      if (!virtualize || !continuousScroll) return

      const scrollEl = ref.current
      if (!scrollEl) return

      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement

        const section = target.closest(
          '.annotationLayer [data-annotation-id]'
        ) as HTMLElement | null
        if (!section) return

        if (!target.closest('a') && !section.querySelector('a')) return

        const annotationId = section.getAttribute('data-annotation-id')
        if (!annotationId) return

        const pageWrapper = section.closest('[data-page]')
        const pageNum = Number(pageWrapper?.getAttribute('data-page'))
        if (!pageNum) return

        const pdfDoc = pdfDocRef.current
        if (!pdfDoc) return

        const link = target.closest('a') as HTMLAnchorElement | null
        if (link) {
          const href = link.getAttribute('href') || ''
          if (
            href.startsWith('http://') ||
            href.startsWith('https://') ||
            href.startsWith('mailto:')
          ) {
            return
          }
        }

        e.preventDefault()
        e.stopPropagation()
        ;(async () => {
          try {
            const page = await pdfDoc.getPage(pageNum)
            const annotations = await page.getAnnotations()
            const annotation = annotations.find(
              (a: { id: string }) => a.id === annotationId
            )

            if (!annotation || annotation.url) return

            let dest: string | unknown[] | null = annotation.dest
            if (!dest) return

            if (typeof dest === 'string') {
              dest = await pdfDoc.getDestination(dest)
            }

            if (Array.isArray(dest) && dest[0]) {
              const pageIndex = await pdfDoc.getPageIndex(dest[0] as PageRef)
              const targetPage = pageIndex + 1

              if (targetPage >= 1 && targetPage <= (numPages ?? 0)) {
                const targetEl = pageRefsMap.current.get(targetPage)
                if (targetEl) {
                  // Instant jump to approximate position. Placeholder heights
                  // are estimates so intermediate pages may shift as they render.
                  targetEl.scrollIntoView({ block: 'start' })

                  // Re-adjust as the target page and its neighbours render and
                  // their heights settle. The ResizeObserver catches the target
                  // itself changing size; the timeouts catch position shifts from
                  // intermediate pages whose heights changed.
                  let adjusting = true
                  const reScroll = () => {
                    if (adjusting) targetEl.scrollIntoView({ block: 'start' })
                  }
                  const obs = new ResizeObserver(reScroll)
                  obs.observe(targetEl)
                  const t1 = setTimeout(reScroll, 150)
                  const t2 = setTimeout(reScroll, 500)
                  setTimeout(() => {
                    adjusting = false
                    obs.disconnect()
                    clearTimeout(t1)
                    clearTimeout(t2)
                  }, 1500)
                }
              }
            }
          } catch {
            // Could not resolve annotation destination
          }
        })()
      }

      scrollEl.addEventListener('click', handleClick, true)
      return () => scrollEl.removeEventListener('click', handleClick, true)
    }, [virtualize, continuousScroll, numPages, ref])

    if (showPagination && continuousScroll) {
      console.warn(
        'showPagination and continuousScroll both set simultaneously'
      )
    }

    const isOcrLoading = React.useMemo(
      () => pageStates.values().some((state) => state.status === 'loading'),
      [pageStates]
    )

    return (
      <ViewerContainer width={width} height={height} className={className}>
        <ScrollContainer ref={ref}>
          <DocumentContainer>
            {loading && (
              <LoadingContainer>
                <Spin size='large' />
              </LoadingContainer>
            )}
            {error && (
              <ErrorContainer>Error loading PDF: {error}</ErrorContainer>
            )}
            <Document
              key={url}
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <LoadingContainer>
                  <Spin size='large' />
                </LoadingContainer>
              }
              error={
                <ErrorContainer>
                  Error loading PDF: {error ?? 'Unknown error'}
                </ErrorContainer>
              }>
              {continuousScroll && numPages && numPages > 0 && pageWidth ? (
                <PagesContainer>
                  {Array.from({ length: numPages }, (_, index) => {
                    const pageNum = index + 1
                    const shouldRender =
                      !virtualize || visiblePagesSet.has(pageNum)
                    const placeholderHeight =
                      pageHeights.current.get(pageNum) || estimatedPageHeight

                    return (
                      <div
                        key={`page_${pageNum}`}
                        ref={setPageRef(pageNum)}
                        data-page={pageNum}
                        style={
                          shouldRender
                            ? undefined
                            : { height: placeholderHeight, width: pageWidth }
                        }>
                        {shouldRender && (
                          <PdfPageWithOcr
                            pageNumber={pageNum}
                            width={pageWidth}
                            url={url}
                            ocrEnabled={ocrEnabled}
                            ocrDebug={ocrDebug}
                            runOcr={runOcr}
                            pageState={pageStates.get(pageNum)}
                            highlights={highlights.filter(
                              (highlight) => highlight.pageNumber === pageNum
                            )}
                            rotate={pageRotations?.[String(index + 1)] ?? 0}
                          />
                        )}
                      </div>
                    )
                  })}
                </PagesContainer>
              ) : pageWidth ? (
                <PageContainer>
                  <PdfPageWithOcr
                    key={`page_${currentPage}`}
                    pageNumber={currentPage}
                    url={url}
                    ocrEnabled={ocrEnabled}
                    ocrDebug={ocrDebug}
                    runOcr={runOcr}
                    pageState={pageStates.get(currentPage)}
                    highlights={highlights.filter(
                      (highlight) => highlight.pageNumber === currentPage
                    )}
                    width={pageWidth}
                    scale={1.0}
                    rotate={pageRotations?.[String(currentPage)] ?? 0}
                  />
                </PageContainer>
              ) : (
                <LoadingContainer>
                  <Spin size='large' />
                </LoadingContainer>
              )}
            </Document>
          </DocumentContainer>
        </ScrollContainer>

        {!continuousScroll && showPagination && numPages && numPages > 1 && (
          <PaginationContainer>
            <ConfigProvider
              theme={{
                token: { colorText: '#000' },
                components: {
                  Pagination: {
                    itemBg: CantileverTheme.colors.grayscale[100],
                    itemLinkBg: CantileverTheme.colors.grayscale[100],
                    itemActiveBg: CantileverTheme.colors.grayscale[300],
                  },
                },
              }}>
              <Pagination
                current={currentPage}
                total={numPages}
                pageSize={1}
                showSizeChanger={false}
                showQuickJumper={false}
                showTotal={(total) => `Page ${currentPage} of ${total}`}
                onChange={handlePageChange}
                size='small'
              />
            </ConfigProvider>
          </PaginationContainer>
        )}

        {!hideFloatActions && (
          <FloatButton.Group
            style={{
              position: 'absolute',
              insetBlockEnd: 12,
              insetInlineEnd: 12,
            }}>
            <FloatButton
              type={ocrEnabled ? 'primary' : 'default'}
              icon={
                isOcrLoading ? <LoadingOutlined spin /> : <FileTextOutlined />
              }
              tooltip={
                ocrEnabled ? 'Text Selection On' : 'Enable Text Selection'
              }
              onClick={() => setOcrEnabled((prev) => !prev)}
            />
            {ocrEnabled && (
              <FloatButton
                type={ocrDebug ? 'primary' : 'default'}
                icon={ocrDebug ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                tooltip={ocrDebug ? 'Hide Text' : 'Show Text'}
                onClick={() => setOcrDebug((prev) => !prev)}
              />
            )}
          </FloatButton.Group>
        )}
      </ViewerContainer>
    )
  }
)

RawPdfViewer.displayName = 'RawPdfViewer'

export const PdfViewer = React.memo(RawPdfViewer)
PdfViewer.displayName = 'PdfViewer'
